import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Locale, TranslationStrings, TranslationKey } from '@/types/i18n'
import { en } from '@/locales/en'
import { am } from '@/locales/am'

const defaultTranslations: Record<Locale, TranslationStrings> = {
  en,
  am
}

interface I18nState {
  locale: Locale
  // Custom overrides per locale (user-edited strings)
  customTranslations: Record<Locale, Partial<TranslationStrings>>

  // Actions
  setLocale: (locale: Locale) => void
  t: (key: TranslationKey) => string
  getTranslation: (key: TranslationKey, locale?: Locale) => string
  updateTranslation: (locale: Locale, key: TranslationKey, value: string) => void
  resetTranslation: (locale: Locale, key: TranslationKey) => void
  resetAllTranslations: (locale: Locale) => void
  getAllTranslations: (locale?: Locale) => TranslationStrings
  getCustomTranslations: (locale?: Locale) => Partial<TranslationStrings>
}

export const useI18nStore = create<I18nState>()(
  persist(
    (set, get) => ({
      locale: 'en',
      customTranslations: {
        en: {},
        am: {}
      },

      setLocale: (locale) => {
        set({ locale })
        // Update document direction for RTL languages if needed in the future
        document.documentElement.lang = locale === 'am' ? 'am-ET' : 'en-US'
      },

      t: (key) => {
        const { locale, customTranslations } = get()
        // Check custom translations first, then fall back to defaults
        return customTranslations[locale][key] ?? defaultTranslations[locale][key] ?? key
      },

      getTranslation: (key, locale) => {
        const currentLocale = locale ?? get().locale
        const { customTranslations } = get()
        return customTranslations[currentLocale][key] ?? defaultTranslations[currentLocale][key] ?? key
      },

      updateTranslation: (locale, key, value) => {
        set((state) => ({
          customTranslations: {
            ...state.customTranslations,
            [locale]: {
              ...state.customTranslations[locale],
              [key]: value
            }
          }
        }))
      },

      resetTranslation: (locale, key) => {
        set((state) => {
          const { [key]: _, ...rest } = state.customTranslations[locale]
          return {
            customTranslations: {
              ...state.customTranslations,
              [locale]: rest
            }
          }
        })
      },

      resetAllTranslations: (locale) => {
        set((state) => ({
          customTranslations: {
            ...state.customTranslations,
            [locale]: {}
          }
        }))
      },

      getAllTranslations: (locale) => {
        const currentLocale = locale ?? get().locale
        const { customTranslations } = get()
        return {
          ...defaultTranslations[currentLocale],
          ...customTranslations[currentLocale]
        }
      },

      getCustomTranslations: (locale) => {
        const currentLocale = locale ?? get().locale
        return get().customTranslations[currentLocale]
      }
    }),
    {
      name: 'satmas-i18n',
      partialize: (state) => ({
        locale: state.locale,
        customTranslations: state.customTranslations
      })
    }
  )
)

// Helper hook for components
export function useTranslation() {
  const { t, locale, setLocale } = useI18nStore()
  return { t, locale, setLocale }
}

// Export default translations for reference
export { defaultTranslations }
